/**
 * Single source of truth for stamp geometry, shared by the server validator and renderer,
 * the front-end PlacementBox/controllers, and the preview renderer — all must agree or a
 * placement saves valid but draws at a different ratio. Three ratios: full (2.0, with
 * metadata), compact (3.0, header + scribble only), and witness (taller, because a witness
 * stamp carries a wrapped address and an occupation the others don't).
 */

import { mmToPt } from '../documentLayout/documentLayoutConstants';
import { MAX_ADDRESS_LINES, STAMP_DESIGN } from './stampDesign';

// The design size the renderer scales everything from: `computeStampLayout` folds
// `widthPt / STAMP_WIDTH_PT` into its px-per-point, so these two set every other ratio.
export const STAMP_WIDTH_PT = 210;
export const STAMP_HEIGHT_PT = 105;

export const STAMP_ASPECT_RATIO = STAMP_WIDTH_PT / STAMP_HEIGHT_PT; // = 2.0; full layout
// = 3.0; used when showStampMetadata=false
export const STAMP_ASPECT_RATIO_COMPACT = STAMP_WIDTH_PT / 70;

// 70pt ≈ 25mm — fits inline fields yet keeps the scribble + text legible at sign
// time. Width floor is shared across both modes.
export const MIN_STAMP_WIDTH_PT = 70;
export const MIN_STAMP_HEIGHT_PT = MIN_STAMP_WIDTH_PT / STAMP_ASPECT_RATIO; // = 35
// Compact pairs to ≈23pt; floored to 28pt so the scribble + header stay readable.
export const MIN_STAMP_HEIGHT_COMPACT_PT = 28;

/** A witness box reserves the worst case: MAX_ADDRESS_LINES of wrapped address plus one
 *  occupation row, beneath the "Witness of X" subtitle — derived from the same
 *  meta.valueFontSize/lineGap tokens `computeStampLayout` scales its rows by, so the box
 *  and the rows it must hold cannot drift. Short addresses leave the reserved space unused
 *  rather than needing a variable row height per stamp. = 177.5pt.
 *
 *  Under-tall is not a soft failure: `computeStampLayout` lays the metadata block out from
 *  the BOTTOM edge upwards, so a box short of this clips the top rows — the witness's own
 *  name included — off the canvas silently. */
export const STAMP_HEIGHT_WITNESS_PT =
	STAMP_HEIGHT_PT +
	(MAX_ADDRESS_LINES + 1) * (STAMP_DESIGN.meta.valueFontSize + STAMP_DESIGN.meta.lineGap);
// ≈1.183. The renderer's row budget is scale-invariant, so only this ratio — never the
// absolute size — decides how many address lines a manually-placed witness stamp gets.
export const STAMP_ASPECT_RATIO_WITNESS = STAMP_WIDTH_PT / STAMP_HEIGHT_WITNESS_PT;
export const MIN_STAMP_HEIGHT_WITNESS_PT = MIN_STAMP_WIDTH_PT / STAMP_ASPECT_RATIO_WITNESS;

// Width/height ratio tolerance; front-end mirrors it so server and client agree.
export const ASPECT_RATIO_TOLERANCE = 0.001;

/** Expected width-to-height ratio for a placement, per whether metadata is rendered and
 *  whether the slot is a witness (which needs the taller box). `isWitness` is ignored in
 *  compact mode, which draws no metadata rows to make room for. */
export function expectedAspectRatio(showMetadata: boolean, isWitness = false): number {
	if (!showMetadata) {
		return STAMP_ASPECT_RATIO_COMPACT;
	}
	return isWitness ? STAMP_ASPECT_RATIO_WITNESS : STAMP_ASPECT_RATIO;
}

/** Minimum stamp height (pt) for the given metadata mode. Width floor is
 *  mode-independent; the height floor tracks whichever ratio applies. */
export function minHeightFor(showMetadata: boolean, isWitness = false): number {
	if (!showMetadata) {
		return MIN_STAMP_HEIGHT_COMPACT_PT;
	}
	return isWitness ? MIN_STAMP_HEIGHT_WITNESS_PT : MIN_STAMP_HEIGHT_PT;
}

/**
 * Design width (pt) of a stamp reserved by a signature ANCHOR. Matches the auto-allocated
 * stamp, and two side by side (420pt) still fit A4's ~483pt printable width, which is what a
 * two-column Word signature block needs.
 */
export const ANCHORED_STAMP_WIDTH_PT = 210;

/** A new date field's width: half a signature box, so the date lands near body-text size. */
export const DEFAULT_DATE_BOX_WIDTH_PT = ANCHORED_STAMP_WIDTH_PT / 2;

/**
 * The space an anchor reserves, in points: the anchored width over whichever ratio applies.
 * A witness needs a taller box when metadata is drawn (its extra rows will not fit the
 * primary's 105pt); with metadata off there is no metadata block at all, so `expectedAspectRatio`
 * ignores `isWitness` and both come out the same compact size. Going through the ratio — the one
 * the placement validator and the front-end resize lock also read — is what keeps a reserved
 * anchor box and a manually-placed box the same shape.
 */
export function anchoredStampSizePt(
	showMetadata: boolean,
	isWitness = false
): { width: number; height: number } {
	return {
		width: ANCHORED_STAMP_WIDTH_PT,
		height: ANCHORED_STAMP_WIDTH_PT / expectedAspectRatio(showMetadata, isWitness),
	};
}

/**
 * The ratio an anchored stamp keeps when an author resizes it.
 *
 * Per anchor, not per document: a witness's metadata block carries more rows, so its box is
 * 210x177.5 (ratio ~1.18) where a primary's is 210x105 (2.0). Resizing against the wrong one
 * clips the top rows — the witness's own name — off an executed deed.
 */
export function anchoredStampAspectRatio(showMetadata: boolean, isWitness = false): number {
	const { width, height } = anchoredStampSizePt(showMetadata, isWitness);
	return width / height;
}

/**
 * An authored anchor width, floored at what stays legible and given its anchor's own ratio.
 *
 * The ceiling is the container's, applied by the renderer — a stamp wider than the column it
 * sits in shrinks to fit, which is the same rule an over-wide default already follows.
 */
export function resizedStampSizePt(
	widthPt: number,
	showMetadata: boolean,
	isWitness = false
): { width: number; height: number } {
	const width = Math.max(MIN_STAMP_WIDTH_PT, widthPt);
	return { width, height: width / anchoredStampAspectRatio(showMetadata, isWitness) };
}

/**
 * The box an anchor reserves. Shared by `flowRender` and the save that sizes a Word master's date
 * placement rows, so the row and the reserved box cannot disagree.
 */
export function anchorBoxSizePt(
	widthMm: number | undefined,
	showMetadata: boolean,
	isWitness = false
): { width: number; height: number } {
	return widthMm === undefined
		? anchoredStampSizePt(showMetadata, isWitness)
		: resizedStampSizePt(mmToPt(widthMm), showMetadata, isWitness);
}

// Fallback geometry for a stamp with no usable saved placement — a witness slot no admin
// ever placed, or a KYC box whose coordinates don't fit the file the investor uploaded.
// Sized to clear the validator's ratio and minimum checks so a defaulted box is
// indistinguishable from a placed one.
export const FALLBACK_STAMP_WIDTH_PT = 140;
export const FALLBACK_STAMP_MARGIN_PT = 50;

/** Bottom-left box (bottom-left origin) for a stamp with no usable saved placement. */
export function bottomLeftStampBox(
	showMetadata: boolean,
	isWitness = false
): {
	x: number;
	y: number;
	width: number;
	height: number;
} {
	return {
		x: FALLBACK_STAMP_MARGIN_PT,
		y: FALLBACK_STAMP_MARGIN_PT,
		width: FALLBACK_STAMP_WIDTH_PT,
		height: FALLBACK_STAMP_WIDTH_PT / expectedAspectRatio(showMetadata, isWitness),
	};
}
